import { buildColumnsObject } from '@shape/lighthouse-components';
import {
  ExtendedVibrationTable,
  HandleAnalysisChange,
  isCellSelected,
} from 'components/Evidences/Helpers/helper';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';
import './AttachVibrationAnalysisTable.scss';
import { Tooltip } from '@shape-ds/components';
import { buildDateColumnsWithCheckbox } from 'components/ExternalReferencesTables/columsConfig';

type UseVibrationAnalysisColumnsProps = {
  data: ExtendedVibrationTable[];
  handleCellSelectionChange?: (params: HandleAnalysisChange) => void;
};

export const useVibrationAnalysisColumns = ({
  data,
  handleCellSelectionChange,
}: UseVibrationAnalysisColumnsProps) => {
  const { t } = useTranslation();
  const dinamicColumns = useMemo(() => {
    const dateKeys =
      data && data.length > 0
        ? Object.keys(data[0]).filter((key) => {
            const value = (data[0] as Record<string, unknown>)[key];
            return (
              key !== 'id' &&
              key !== 'description' &&
              value &&
              typeof value === 'object' &&
              value !== null &&
              'collection_date' in value
            );
          })
        : [];
    return dateKeys;
  }, [data]);

  const baseColumns = {
    id: {
      enableSorting: false,
      size: 160,
      header: t('monitoring:tables.columns.VIBRATION_ANALYSIS.id'),
      accessorFn: (data: ExtendedVibrationTable) => data.id || '',
      cell: (cell: CellContext<ExtendedVibrationTable, unknown>) => {
        const idValue = cell.getValue() as string;
        return <Tooltip title={idValue}>{idValue}</Tooltip>;
      },
    },
  };

  return {
    columns: [
      ...buildColumnsObject<ExtendedVibrationTable>({
        columns: baseColumns,
        getLabel: (key: string) => t(`monitoring:tables.columns.VIBRATION_ANALYSIS.${key}`),
      }),
      ...buildDateColumnsWithCheckbox(dinamicColumns, {
        checkboxConfig: handleCellSelectionChange
          ? {
              isCellSelected,
              handleCellSelectionChange,
              getDescription: (row: ExtendedVibrationTable) => row.id || '',
            }
          : undefined,
      }),
    ],
  };
};
